import { useState, useEffect, useCallback, useRef } from 'react'
import type { UUID } from 'crypto'
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material'
import { useCreateApplicationMutation } from '@/utils/hooks/applications'
import { getApplicationsByNeed } from '@/api/applicationsAPI'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useJobRunner } from '../hooks/useJobRunner'
import { useToken } from '../hooks/useToken'
import ProgressScreen from '../components/ProgressScreen'
import { primaryButtonSx } from '../styles'
import { COLORS } from '../theme'

const HELP_TEXTS = [
  'Fundsorter\'s database includes the questions from funder application forms. Using the info you\'ve provided, the system creates a draft of answers to the application questions for you.',
  'Fundsorter doesn\'t send anything to funders directly \u2014 it\'s up to you to complete the draft and submit.',
]

export default function DraftGenerating() {
  const { orgUuid, fundingNeedId, scanResultId, setApplicationId, goNext, goBack, setHideNav, staleFrom, setStaleFrom } = useOnboardingV2()
  const createApp = useCreateApplicationMutation((orgUuid as UUID) ?? null)
  const { getToken } = useToken()

  // Abort guard — prevents post-unmount goNext/persist calls
  const unmountedRef = useRef(false)
  useEffect(() => () => { unmountedRef.current = true }, [])

  // Pre-flight state: check for existing application before starting a new one
  const [checking, setChecking] = useState(true)
  const [existingAppId, setExistingAppId] = useState<string | null>(null)

  const startJob = useCallback(async (token: string) => {
    const result = await createApp.mutateAsync({ token, scanId: parseInt(scanResultId!) })
    return result.job_id
  }, [scanResultId, createApp])

  const onComplete = useCallback(async (token: string) => {
    if (unmountedRef.current) return
    if (staleFrom !== null && staleFrom <= STEPS.DRAFT_GENERATING) {
      setStaleFrom(null)
    }
    // Resolve applicationId before advancing
    if (fundingNeedId && orgUuid) {
      try {
        const apps = await getApplicationsByNeed(token, fundingNeedId, orgUuid as UUID)
        if (unmountedRef.current) return
        if (apps.length > 0) {
          setApplicationId(String(apps[0].id))
        }
      } catch {
        // Non-blocking — DraftComplete has fallback resolution
      }
    }
    await goNext()
  }, [fundingNeedId, orgUuid, setApplicationId, goNext, staleFrom, setStaleFrom])

  const { progress, error, start, retry } = useJobRunner({
    sseType: 'application',
    startJob,
    onComplete,
    prerequisiteError: !scanResultId ? 'No match selected' : null,
  })

  useEffect(() => {
    setHideNav(true)
    return () => setHideNav(false)
  }, [setHideNav])

  // Pre-flight: check for existing application, then either show info screen or start job
  useEffect(() => {
    async function init() {
      if (fundingNeedId && orgUuid) {
        try {
          const token = await getToken()
          if (unmountedRef.current) return
          const apps = await getApplicationsByNeed(token, fundingNeedId, orgUuid as UUID)
          if (unmountedRef.current) return
          if (apps.length > 0) {
            setExistingAppId(String(apps[0].id))
            setChecking(false)
            return
          }
        } catch {
          // Lookup failed — fall through to normal job start
        }
      }
      if (!unmountedRef.current) {
        setChecking(false)
        start()
      }
    }
    init()
  }, [start])

  // Loading while pre-flight runs
  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: COLORS.primary }} />
      </Box>
    )
  }

  // Existing application found — show info screen like ScanRunning's payment guard
  if (existingAppId) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 4,
          textAlign: 'center',
        }}
      >
        <Alert severity="info" sx={{ mb: 3, maxWidth: 480 }}>
          You already have a draft application for this funding need.
        </Alert>
        <Typography
          variant="body2"
          sx={{ mb: 3, maxWidth: 480, color: 'text.secondary' }}
        >
          Click below to view your existing draft and continue where you left
          off.
        </Typography>
        <Button
          disableRipple
          variant="contained"
          onClick={() => {
            setApplicationId(existingAppId)
            goNext()
          }}
          sx={primaryButtonSx}
        >
          View your draft
        </Button>
      </Box>
    )
  }

  return (
    <ProgressScreen
      statusText="Drafting your application..."
      helpTexts={HELP_TEXTS}
      progress={progress}
      error={error}
      onRetry={retry}
      onBack={() => goBack()}
    />
  )
}
